import {
  Check,
  ChefHat,
  ClipboardCheck,
  ScrollText,
  Sparkles,
} from "lucide-react";
import { motion } from "framer-motion";
import { Eyebrow, Rule } from "@/components/editorial";
import { cn } from "@/lib/utils";
import type { UsagePayload, UsageSummary } from "@/types/brigade";
import type { BrigadeRole } from "@/types/brigade";

const ROLES: ReadonlyArray<{
  id: BrigadeRole;
  label: string;
  Icon: typeof ChefHat;
}> = [
  { id: "nutritionist", label: "Nutritionniste", Icon: ScrollText },
  { id: "chef", label: "Chef exécutif", Icon: ChefHat },
  { id: "sous_chef", label: "Experts & planification", Icon: Sparkles },
  { id: "qa", label: "Contrôle qualité", Icon: ClipboardCheck },
];

// v2 roles that map to the "sous_chef" UI step (expert fan-out + refiner + batch assembly)
const V2_SOUS_CHEF_ROLES = new Set([
  "accumulator",
  "refiner",
  "final_gate",
  "batch_plan",
  "assembler",
]);

const TOKEN_FMT = new Intl.NumberFormat("fr-FR");

/**
 * Brigade live progress — drives off `useBrigadeStream` state.
 *
 * Renders the 4-role checklist plus a token meter that grows as each
 * agent finishes its LLM call. The meter is `usageRollup` while the
 * brigade is running and `usageSummary` once it completes.
 */
export function BrigadeProgress({
  currentRole,
  completedRoles,
  error,
  usageByRole,
  usageRollup,
  usageSummary,
}: {
  currentRole: string | null;
  completedRoles: Set<string>;
  error: string | null;
  usageByRole?: Partial<Record<string, UsagePayload>>;
  usageRollup?: UsageSummary | null;
  usageSummary?: UsageSummary | null;
}) {
  const total = usageSummary ?? usageRollup ?? null;

  // v2 brigade uses different role names (expert, refiner, batch_plan, ...).
  // Map v2 intermediate roles onto the 4 display steps.
  const v1Roles: BrigadeRole[] = ["nutritionist", "chef", "sous_chef", "qa"];
  const hasV2Roles = [...completedRoles].some(
    (r) => !v1Roles.includes(r as BrigadeRole),
  );
  const effectiveCompleted = new Set(completedRoles);
  // If assembler or batch_plan completed, the whole sous_chef phase is done
  if (hasV2Roles && (completedRoles.has("assembler") || completedRoles.has("batch_plan"))) {
    effectiveCompleted.add("sous_chef");
  }

  // v2: show sous_chef as active when any intermediate role is running
  const isV2SousChefActive =
    currentRole !== null &&
    (V2_SOUS_CHEF_ROLES.has(currentRole) ||
      currentRole.startsWith("day_enricher_") ||
      // expert roles (restrictions, conservation, etc.)
      (!v1Roles.includes(currentRole as BrigadeRole) && !V2_SOUS_CHEF_ROLES.has(currentRole)));

  const effectiveCurrentRole =
    isV2SousChefActive && !v1Roles.includes(currentRole as BrigadeRole)
      ? "sous_chef"
      : currentRole;
  return (
    <div className="border border-hair bg-paper p-5">
      <div className="mb-4 flex items-baseline justify-between">
        <Eyebrow accent>La brigade en cuisine</Eyebrow>
        <span className="text-[11px] text-ink-50">
          {ROLES.filter((r) => effectiveCompleted.has(r.id)).length} /{" "}
          {ROLES.length} étapes
        </span>
      </div>
      <Rule className="mb-4" />
      <ul className="space-y-3">
        {ROLES.map(({ id, label, Icon }) => {
          const done = effectiveCompleted.has(id);
          const active = !done && effectiveCurrentRole === id;
          // For sous_chef in v2, aggregate tokens from all intermediate roles
          const usage =
            id === "sous_chef" && usageByRole
              ? _aggregateV2SousChefUsage(usageByRole)
              : usageByRole?.[id];
          return (
            <li
              key={id}
              className={cn(
                "flex items-center gap-3",
                done
                  ? "text-foreground"
                  : active
                    ? "text-foreground"
                    : "text-ink-30",
              )}
            >
              <span
                className={cn(
                  "relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full border",
                  done
                    ? "border-accent bg-accent text-background"
                    : active
                      ? "border-foreground bg-background text-foreground"
                      : "border-hair bg-background text-ink-30",
                )}
              >
                {done ? (
                  <Check className="size-3.5" strokeWidth={2.5} />
                ) : (
                  <Icon className="size-3.5" strokeWidth={1.5} />
                )}
                {active && (
                  <motion.span
                    className="absolute inset-0 rounded-full ring-2 ring-foreground/40"
                    animate={{ opacity: [0.4, 0.05, 0.4] }}
                    transition={{
                      duration: 1.6,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                )}
              </span>
              <span
                className={cn(
                  "flex-1 font-serif text-[15px]",
                  active && "italic",
                  done && "line-through opacity-70",
                )}
              >
                {label}
              </span>
              {usage && (
                <span className="font-mono text-[10.5px] text-ink-50 tabular-nums">
                  {TOKEN_FMT.format(usage.total_tokens)} tok
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {total && total.calls > 0 && (
        <>
          <Rule className="my-4" />
          <TotalMeter total={total} live={!usageSummary} />
        </>
      )}

      {error && <p className="mt-4 text-[12px] text-destructive">{error}</p>}
    </div>
  );
}

function _aggregateV2SousChefUsage(
  usageByRole: Partial<Record<string, UsagePayload>>,
): UsagePayload | undefined {
  const relevant = Object.entries(usageByRole).filter(
    ([role]) =>
      V2_SOUS_CHEF_ROLES.has(role) ||
      role.startsWith("day_enricher_") ||
      // expert roles not in the 4 v1 roles
      (!["nutritionist", "chef", "sous_chef", "qa"].includes(role) &&
        !V2_SOUS_CHEF_ROLES.has(role)),
  );
  if (relevant.length === 0) return undefined;
  const totals = relevant.reduce(
    (acc, [, u]) => {
      if (!u) return acc;
      return {
        ...acc,
        input_tokens: acc.input_tokens + u.input_tokens,
        cached_tokens: acc.cached_tokens + u.cached_tokens,
        output_tokens: acc.output_tokens + u.output_tokens,
        total_tokens: acc.total_tokens + u.total_tokens,
        duration_seconds: acc.duration_seconds + u.duration_seconds,
      };
    },
    {
      workflow: relevant[0][1]?.workflow ?? "",
      executor: "sous_chef",
      input_tokens: 0,
      cached_tokens: 0,
      output_tokens: 0,
      total_tokens: 0,
      duration_seconds: 0,
    } as UsagePayload,
  );
  return totals;
}

function TotalMeter({ total, live }: { total: UsageSummary; live: boolean }) {
  const cachePct =
    total.input_tokens > 0
      ? Math.round((total.cached_tokens / total.input_tokens) * 100)
      : 0;
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <div className="flex items-baseline gap-2">
        <span className="text-[10.5px] uppercase tracking-[0.18em] text-ink-50">
          {live ? "En cours" : "Total"}
        </span>
        <span className="font-serif text-[22px] font-normal tabular-nums">
          {TOKEN_FMT.format(total.total_tokens)}
        </span>
        <span className="text-[11px] text-ink-50">tokens</span>
      </div>
      <div className="flex gap-3 font-mono text-[10.5px] text-ink-50">
        <span>📥 {TOKEN_FMT.format(total.input_tokens)}</span>
        {total.cached_tokens > 0 && <span>(cache {cachePct}%)</span>}
        <span>📤 {TOKEN_FMT.format(total.output_tokens)}</span>
        <span>⏱ {total.duration_seconds.toFixed(1)}s</span>
      </div>
    </div>
  );
}
